import { StoreCreditIcon } from "@xola/icons";
import { Table } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import { sumBy } from "lodash";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { PaymentMethod, Permission, Product, PurchaseItemObject, Source, SourceCopy, Transaction } from "types";
import { Currency } from "@/components/Common/Currency";
import { InfiniteList } from "@/components/Common/InfiniteList";
import { PaymentMethodIcon } from "@/components/Common/PaymentMethodIcon";
import { ProductImage, ProductImageProps } from "@/components/Common/ProductImage";
import { SourceIcon, SourceName } from "@/components/Common/SourceIcon";
import { ColumnLayout } from "@/components/Layout/ColumnLayout";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useResource } from "@/hooks/useResource";
import { isAmountIncludesPartnerFee, isPaidByTraveler, showPartnerFee } from "@/models/PartnerFeeFormula";
import { getPaymentMethodName } from "@/models/Payment";
import { getGuestFee, getMerchandiseFee, getPartnerFee, getProcessingFee, purchaseTypes } from "@/models/Transaction";
import { useAnalyticsFilterStore } from "@/stores/reports/analyticsFilterStore";

export const AnalyticsReportTable = () => {
    const seller = useSeller();
    const history = useHistory();
    const { isGranted } = useAuthorization();
    const filters = useAnalyticsFilterStore((state) => state.filters);
    const dateParams = useAnalyticsFilterStore((state) => state.getDateParams());

    const params: any = { "type[in]": purchaseTypes.join(","), ...dateParams };
    if (filters.products.selected.length > 0) {
        params["items.orderItem.product[in]"] = filters.products.selected.join(",");
    }

    const showServiceFee = showPartnerFee();
    const showGuestFee = useMemo(() => {
        return isAmountIncludesPartnerFee(seller.partnerFeeFormulas) && isPaidByTraveler(seller.partnerFeeFormulas);
    }, [seller]);
    const showMerchandiseFee = isGranted(Permission.MERCHANDISE_VIEW);

    const handleOpenPurchase = (purchaseId?: string) => {
        if (!purchaseId) {
            return;
        }

        history.push(`/purchases/${purchaseId}`);
    };

    const {
        data: transactions,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useInfiniteCollection<Transaction>("transactions", params);

    return (
        <ColumnLayout.Fill>
            <InfiniteList hasMore={hasNextPage ?? false} isLoading={isFetchingNextPage} onLoadMore={fetchNextPage}>
                <Table>
                    <Table.Head>
                        <Table.Row>
                            <Table.Header>Item</Table.Header>
                            <Table.Header>Date</Table.Header>
                            <Table.Header>Gross</Table.Header>
                            <Table.Header>Processing Fee</Table.Header>
                            {showServiceFee && <Table.Header>Service Fee</Table.Header>}
                            {showGuestFee && <Table.Header>Guest Fee</Table.Header>}
                            {showMerchandiseFee && <Table.Header>Merchandise Fee</Table.Header>}
                            <Table.Header>Taxes/Fees</Table.Header>
                            <Table.Header>Net</Table.Header>
                            <Table.Header>Balance</Table.Header>
                            <Table.Header>Source</Table.Header>
                            <Table.Header>Method</Table.Header>
                        </Table.Row>
                    </Table.Head>

                    <Table.Body isStriped>
                        {transactions.map((transaction) => {
                            const { items } = transaction;

                            const taxFee = sumBy(items, "taxFee");
                            const gross = sumBy(items, "gross");
                            const net = sumBy(items, "net");
                            const serviceFee = getPartnerFee(transaction);
                            const processingFee = getProcessingFee(transaction);
                            const guestFee = getGuestFee(transaction);
                            const merchandiseFee = getMerchandiseFee(transaction);
                            const { product } = transaction.items[0].orderItem ?? {};
                            const isStoreCredit = transaction.method === PaymentMethod.STORE_CREDIT;
                            const isGift = transaction.source === Source.GIFT;

                            return (
                                <Table.Row
                                    key={transaction.id}
                                    className="cursor-pointer"
                                    onClick={() => handleOpenPurchase(transaction.purchase?.id)}
                                >
                                    <Table.Cell>
                                        {product ? (
                                            <ResourceImage product={product} size="small" />
                                        ) : isStoreCredit ? (
                                            <StoreCreditIcon size="large" className="ml-1" />
                                        ) : (
                                            // displays a gift icon for transactions with a gift source and a dummy image for transactions without any source
                                            <ProductImage
                                                product={product}
                                                size="small"
                                                object={isGift ? PurchaseItemObject.GIFT : undefined}
                                            />
                                        )}
                                    </Table.Cell>
                                    <Table.Cell className="no-translate">
                                        {formatDate(transaction.createdAt, DateFormat.DATE_SHORT)}
                                    </Table.Cell>
                                    <Table.Cell className={clsx(gross < 0 ? "text-red" : "")}>
                                        <Currency>{gross}</Currency>
                                    </Table.Cell>
                                    <Table.Cell className={clsx(processingFee < 0 ? "text-red" : "")}>
                                        <Currency>{processingFee}</Currency>
                                    </Table.Cell>
                                    {showServiceFee && (
                                        <Table.Cell className={clsx(serviceFee < 0 ? "text-red" : "")}>
                                            <Currency>{serviceFee}</Currency>
                                        </Table.Cell>
                                    )}
                                    {showGuestFee && (
                                        <Table.Cell className={clsx(guestFee < 0 ? "text-red" : "")}>
                                            <Currency>{guestFee}</Currency>
                                        </Table.Cell>
                                    )}
                                    {showMerchandiseFee && (
                                        <Table.Cell className={clsx(merchandiseFee < 0 ? "text-red" : "")}>
                                            <Currency>{merchandiseFee}</Currency>
                                        </Table.Cell>
                                    )}
                                    <Table.Cell className={clsx(taxFee < 0 ? "text-red" : "")}>
                                        <Currency>{taxFee}</Currency>
                                    </Table.Cell>
                                    <Table.Cell className={clsx(net < 0 ? "text-red" : "")}>
                                        <Currency>{net}</Currency>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Currency>{transaction.balance}</Currency>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {transaction.source && (
                                            <>
                                                <SourceIcon
                                                    source={transaction.source}
                                                    size="small"
                                                    className="top-0 mr-2"
                                                />
                                                {SourceCopy[transaction.source] ?? (
                                                    <SourceName source={transaction.source} />
                                                )}
                                            </>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <PaymentMethodIcon
                                            method={transaction.method}
                                            size="small"
                                            className="top-0 mr-2"
                                        />

                                        {getPaymentMethodName(seller, transaction.method)}
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </InfiniteList>
        </ColumnLayout.Fill>
    );
};

const ResourceImage = ({ product: reference, ...rest }: ProductImageProps) => {
    const { data: product } = useResource<Product>(
        ["products", reference.id],
        {},
        {
            enabled: !("object" in reference),
        },
    );

    return <ProductImage product={product ?? reference} {...rest} />;
};
